/**
 * Builds the `data` payload of an `input` event.
 *
 * W9 (docs/CMS_REPLY_W9_REVEAL.md §0): the recorder sends RAW values plus the
 * classification signals the page itself exposes. The server-side ingestion
 * classifier is the ONLY authority on what persists in plaintext; the recorder
 * is untrusted. Client-side masking of hardstop and password fields stays as
 * defence in depth, never as the control.
 */
export interface InputLike {
  tagName: string;
  type?: string;
  name?: string;
  id?: string;
  value: string;
  getAttribute(name: string): string | null;
}

export interface InputEventData {
  value: string;
  type: string;
  name: string;
  id: string;
  autocomplete: string;
  label: string;
  /**
   * v3 signals. Builder-generated forms (Gravity Forms and friends) give the
   * input an opaque `name`/`id`, no `autocomplete`, and a label whose
   * textContent has the required indicator run into it ("First(Required)").
   * The placeholder ("First Name") is then the clearest statement of intent
   * the page makes, and the aria-label is the accessible name when there is
   * no visible label at all. Both are advisory, like every other signal: the
   * server-side classifier decides, and the value must still confirm.
   */
  placeholder: string;
  ariaLabel: string;
  /** true when the client masked the value (hardstop or password). */
  masked: boolean;
}

export interface InputSignalOptions {
  hardstop: (fieldName: string) => boolean;
  trackSensitiveData: boolean;
  findLabel: (el: InputLike) => string;
}

export function buildInputEventData(target: InputLike, opts: InputSignalOptions): InputEventData | null {
  const type = (target.type || 'text').toLowerCase();
  if (type === 'password' && !opts.trackSensitiveData) return null;

  const name = target.name || target.id || '';
  const id = target.id || '';
  const raw = typeof target.value === 'string' ? target.value : '';
  const mustMask = type === 'password' || opts.hardstop(name) || (id !== '' && opts.hardstop(id));

  return {
    value: mustMask ? '*'.repeat(raw.length) : raw,
    type,
    name,
    id,
    autocomplete: target.getAttribute('autocomplete') || '',
    label: opts.findLabel(target),
    placeholder: target.getAttribute('placeholder') || '',
    ariaLabel: target.getAttribute('aria-label') || '',
    masked: mustMask,
  };
}
